// src/pages/CategoriesPage.js
import React, { useEffect, useState } from 'react';
import api from '../api/axios';

import toast from 'react-hot-toast';

const CategoriesPage = () => {
  const [categories, setCategories] = useState([]);
  const [name, setName] = useState('');
  const [editingId, setEditingId] = useState(null);
  const [formError, setFormError] = useState('');
  const [formSuccess, setFormSuccess] = useState('');

  const fetchCategories = async () => {
    try {
      const res = await api.get('/categories');
      setCategories(res.data);
    } catch (err) {
      toast.error('Failed to load categories');
    }
  };

  useEffect(() => {
    fetchCategories();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    const trimmed = name.trim();
    if (!trimmed) {
      setFormError('Category name is required.');
      return;
    }
    if (trimmed.length > 120) {
      setFormError('Category name should be 120 characters or fewer.');
      return;
    }

    setFormError('');

    try {
      if (editingId) {
        await api.put(`/categories/${editingId}`, { name: trimmed });
        toast.success('Category updated');
        setFormSuccess('Category updated.');
      } else {
        await api.post('/categories', { name: trimmed });
        toast.success('Category created');
        setFormSuccess('Category created.');
      }

      setName('');
      setEditingId(null);
      fetchCategories();
      setTimeout(() => setFormSuccess(''), 2500);
    } catch (err) {
      const message = err.response?.data?.error || 'Error saving category';
      toast.error(message);
      setFormError(message);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this category?')) return;

    try {
      await api.delete(`/categories/${id}`);
      toast.success('Category deleted');
      fetchCategories();
    } catch (err) {
      toast.error('Error deleting category');
    }
  };

  const startEdit = (cat) => {
    setEditingId(cat.id);
    setName(cat.name);
    setFormError('');
    setFormSuccess('');
  };

  return (
    <div className="flex h-[calc(100vh-4rem)] flex-col gap-6 overflow-auto bg-slate-950 px-6 py-6 text-slate-100">
      <h1 className="text-2xl font-semibold tracking-tight text-slate-100">Manage Categories</h1>

      <div className="grid gap-6 lg:grid-cols-[1.1fr,1.4fr]">
        <section className="rounded-3xl border border-slate-800 bg-slate-900/60 p-6 shadow-[0_25px_60px_-30px_rgba(8,15,26,0.9)]">
          <header className="mb-4 space-y-1">
            <h2 className="text-lg font-semibold text-slate-100">
              {editingId ? 'Update category' : 'Create category'}
            </h2>
            <p className="text-sm text-slate-400">
              Categories help group products on the sales screen and in reports.
            </p>
          </header>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <label className="text-xs font-semibold uppercase tracking-wide text-slate-400">
                Category name
              </label>
              <input
                type="text"
                value={name}
                onChange={(e) => {
                  setName(e.target.value);
                  setFormError('');
                }}
                className={`w-full rounded-2xl border px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-brandGreen/30 ${
                  formError
                    ? 'border-rose-500/60 bg-rose-950/40 text-rose-100 placeholder:text-rose-300/60'
                    : 'border-slate-800 bg-slate-950/70 text-slate-100 placeholder:text-slate-500'
                }`}
                placeholder="e.g. Fresh Produce"
                maxLength={160}
              />
              {formError && <p className="text-xs text-rose-300">{formError}</p>}
              {formSuccess && !formError && (
                <p className="text-xs text-emerald-300">{formSuccess}</p>
              )}
            </div>

          <div className="flex items-center gap-3 pt-2">
              <button
                type="submit"
                className="inline-flex items-center justify-center rounded-full bg-brandGreen px-5 py-2 text-sm font-semibold uppercase tracking-wide text-white transition hover:bg-brandDark"
              >
                {editingId ? 'Update category' : 'Add category'}
              </button>
              {editingId && (
                <button
                  type="button"
                  onClick={() => {
                    setEditingId(null);
                    setName('');
                    setFormError('');
                    setFormSuccess('');
                  }}
                  className="inline-flex items-center justify-center rounded-full border border-slate-700 px-5 py-2 text-sm font-semibold uppercase tracking-wide text-slate-200 transition hover:border-slate-500 hover:text-white"
                >
                  Cancel
                </button>
              )}
            </div>
          </form>
        </section>

        <section className="rounded-3xl border border-slate-800 bg-slate-900/60 p-6 shadow-[0_25px_60px_-30px_rgba(8,15,26,0.9)]">
          <header className="mb-4 flex items-center justify-between">
            <h2 className="text-lg font-semibold text-slate-100">Current categories</h2>
            <span className="text-xs text-slate-500">{categories.length} total</span>
          </header>

          <ul className="divide-y divide-slate-800">
            {categories.map((cat) => (
              <li
                key={cat.id}
                className="flex items-center justify-between py-3 text-sm text-slate-200"
              >
                <span className="truncate">{cat.name}</span>
                <div className="flex items-center gap-3 text-xs uppercase tracking-wide">
                  <button
                    onClick={() => startEdit(cat)}
                    className="rounded-full border border-brandGreen/50 px-3 py-1 text-brandGreen transition hover:bg-brandGreen/10"
                  >
                    Edit
                  </button>
                  <button
                    onClick={() => handleDelete(cat.id)}
                    className="rounded-full border border-rose-500/50 px-3 py-1 text-rose-300 transition hover:bg-rose-500/10"
                  >
                    Delete
                  </button>
                </div>
              </li>
            ))}
            {categories.length === 0 && (
              <li className="py-6 text-center text-xs text-slate-500">
                No categories yet. Create your first category to keep the product grid organised.
              </li>
            )}
          </ul>
        </section>
      </div>
    </div>
  );
};

export default CategoriesPage;
